import React from 'react';

import { Button, CheckGroupItem, useClickAway } from '@/ui-kit';
import type { DayOfWeek } from '@/typings';
import { useFormActions, useFormValues } from '../../core';
import {
  FormFieldType,
  type FormInitialValues,
  type OfficeHoursDays,
} from '../../typings';

import './CopyHoursPopover.scss';

type CopyHoursPopoverProps = {
  days: OfficeHoursDays;
  sourceDayKey: DayOfWeek;
  disabled?: boolean;
};

export type CopyFieldUpdate = { fieldName: string; value: unknown };

/**
 * Builds the field/value pairs for copying one day's fields onto one or more
 * target days. Pure and exported so it's testable without component-test infra.
 *
 * A source value of `undefined` (the adapter's representation of an unfilled
 * Time field) is coerced into the same "explicit empty" a manual clear would
 * produce (`''` for Time, `false` for Checkbox) — passing `undefined` straight
 * through would leave the change set with only `undefined` values when the
 * target's other fields already match, which downstream change-detection
 * treats as "no meaningful changes" and silently drops.
 */
export const buildCopyFieldUpdates = (
  days: OfficeHoursDays,
  sourceDayKey: DayOfWeek,
  targetDayKeys: DayOfWeek[],
  values: Record<string, unknown>,
): CopyFieldUpdate[] => {
  const sourceFields = days[sourceDayKey].fields;
  const updates: CopyFieldUpdate[] = [];

  targetDayKeys.forEach(targetDayKey => {
    const targetFields = days[targetDayKey].fields;
    // Every day's fields array has the same shape (From, To, OnCall?, Closed?) —
    // built identically by the adapter — so positional matching is safe here.
    sourceFields.forEach((sourceField, index) => {
      const targetField = targetFields[index];
      if (targetField) {
        const sourceValue = values[sourceField.name];
        const coerced =
          sourceValue ??
          (sourceField.type === FormFieldType.Checkbox ? false : '');
        updates.push({ fieldName: targetField.name, value: coerced });
      }
    });
  });

  return updates;
};

export const CopyHoursPopover: React.FC<CopyHoursPopoverProps> = ({
  days,
  sourceDayKey,
  disabled,
}) => {
  const [open, setOpen] = React.useState(false);
  const [selectedDays, setSelectedDays] = React.useState<DayOfWeek[]>([]);
  const values = useFormValues<FormInitialValues>();
  const { setFieldValue } = useFormActions();
  const popoverRef = useClickAway(() => setOpen(false));

  const otherDayKeys = (Object.keys(days) as DayOfWeek[]).filter(
    dayKey => dayKey !== sourceDayKey,
  );

  const toggleTargetDay = (dayKey: DayOfWeek) => {
    setSelectedDays(prev =>
      prev.includes(dayKey)
        ? prev.filter(d => d !== dayKey)
        : [...prev, dayKey],
    );
  };

  const applyCopy = () => {
    const updates = buildCopyFieldUpdates(
      days,
      sourceDayKey,
      selectedDays,
      values,
    );
    updates.forEach(({ fieldName, value }) => setFieldValue(fieldName, value));

    setSelectedDays([]);
    setOpen(false);
  };

  if (otherDayKeys.length === 0) return null;

  return (
    <div className="copy-hours-popover" ref={popoverRef}>
      <Button
        type="button"
        variant="tertiary"
        size="small"
        disabled={disabled}
        onClick={() => setOpen(prev => !prev)}
      >
        Copy to...
      </Button>
      {open && (
        <div className="copy-hours-popover__panel">
          {otherDayKeys.map(dayKey => (
            <CheckGroupItem
              key={dayKey}
              scale="small"
              group={`copyHoursTarget_${sourceDayKey}`}
              id={`copyHoursTarget_${sourceDayKey}_${dayKey}`}
              label={days[dayKey].name}
              checked={selectedDays.includes(dayKey)}
              indeterminate={false}
              onChange={() => toggleTargetDay(dayKey)}
            />
          ))}
          <Button
            type="button"
            variant="primary"
            size="small"
            disabled={selectedDays.length === 0}
            onClick={applyCopy}
          >
            Apply
          </Button>
        </div>
      )}
    </div>
  );
};
